import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { AlertTriangle, User, Mail } from 'lucide-react'
import { type UserWithRoles } from 'lib/api'

interface DeleteUserModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  user: UserWithRoles | null
  loading?: boolean
}

export function DeleteUserModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  user, 
  loading = false 
}: DeleteUserModalProps) {
  const { t } = useTranslation()

  if (!user) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.deleteUser')}
      className="max-w-md"
    >
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full">
          <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>

        {/* Warning Message */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('users.messages.confirmDelete')}
          </h3>
          <p className="text-muted-foreground">
            {t('users.messages.deleteWarning')}
          </p>
        </div>

        {/* User Info */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">
                {user.full_name || user.email}
              </p>
              <p className="text-sm text-muted-foreground flex items-center">
                <Mail className="h-3 w-3 mr-1" />
                {user.email}
              </p>
              {user.roles.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('users.roles')}: {user.roles.map(role => role.role_name).join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Warning Box */}
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            {t('users.messages.deleteWarningDetails')}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            loading={loading}
          >
            {t('users.deleteUser')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
